/**
 * Create an account (CLAUDE.md 11.6).
 *
 * Registration creates the user *and* their personal workspace in one transaction --
 * an account without a workspace must never exist (ADR-0002). That is the backend's
 * job; this page only has to not get in the way of it.
 *
 * An invitation token in the URL is passed through, because a valid invitation is
 * itself the authorisation to register even when open registration is off. That is
 * how a firm onboards staff on a closed instance (11.6).
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { useAuth } from '@/auth/AuthProvider'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Button } from '@/components/ui/button'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'

const MIN_PASSWORD_LENGTH = 12

export function RegisterPage() {
  const { t, i18n } = useTranslation(['auth', 'common'])
  const { register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const invitationToken = searchParams.get('invitation')

  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    display_name: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const passwordTooShort = form.password.length > 0 && form.password.length < MIN_PASSWORD_LENGTH

  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (passwordTooShort) return
    setPending(true)
    setError(null)
    try {
      await register({
        ...form,
        display_name: form.display_name || undefined,
        locale: i18n.language.slice(0, 2),
        invitation_token: invitationToken ?? undefined,
      })
      navigate('/', { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.localised : String(caught))
    } finally {
      setPending(false)
    }
  }

  return (
    <AuthLayout title={t('register.title')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <Field>
          <FieldLabel>{t('register.email')}</FieldLabel>
          <FieldInput
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            value={form.email}
            onChange={update('email')}
          />
        </Field>

        <Field>
          <FieldLabel>{t('register.username')}</FieldLabel>
          <FieldInput
            type="text"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            pattern="[A-Za-z0-9._-]+"
            minLength={3}
            required
            value={form.username}
            onChange={update('username')}
          />
        </Field>

        <Field>
          <FieldLabel hint={t('common:common.optional')}>{t('register.display_name')}</FieldLabel>
          <FieldInput
            type="text"
            autoComplete="name"
            value={form.display_name}
            onChange={update('display_name')}
          />
        </Field>

        <Field
          description={t('register.password_hint')}
          error={
            passwordTooShort
              ? t('error.auth.password_too_short', { minimum: MIN_PASSWORD_LENGTH })
              : null
          }
        >
          <FieldLabel>{t('register.password')}</FieldLabel>
          <FieldInput
            type="password"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
            value={form.password}
            onChange={update('password')}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          className="w-full"
          loading={pending}
          disabled={passwordTooShort}
        >
          {t('register.submit')}
        </Button>

        <p className="text-center text-sm">
          <Link to="/login" className="text-accent hover:underline">
            {t('register.have_account')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
